import { useCallback } from '@wordpress/element';
import { api, invalidateCacheByPattern } from '../../helpers/api';

interface PricingCalculationOptions {
	formId: number;
	serviceId: number;
}

// Module-level map to track in-flight requests per form+field
// This prevents race conditions when parameters change rapidly (e.g., occupancy updates)
const requestMap = new Map<
	string,
	{
		promise: Promise<number>;
		controller: AbortController;
		params: string; // JSON stringified params for comparison
	}
>();

export function usePricingCalculation({
	formId,
	serviceId,
}: PricingCalculationOptions) {
	const calculatePrice = useCallback(
		async (data: {
			resource_ids?: number[];
			start_time?: string;
			end_time?: string;
			quantity?: number;
			occupancy?: number;
			field_id: number;
		}): Promise<number> => {
			// Don't send request if we don't have a start time as it will 400
			if (!data.start_time) {
				return 0;
			}

			const pricingData = {
				service_id: serviceId,
				form_id: formId,
				...data,
			};

			// Create unique key for this form+field combination
			const requestKey = `${formId}:${data.field_id}`;
			const paramsKey = JSON.stringify(pricingData);

			// Check if there's an in-flight request for this form+field
			const existing = requestMap.get(requestKey);
			if (existing) {
				// If params are identical, return the existing promise
				if (existing.params === paramsKey) {
					return existing.promise;
				}
				// Params changed, abort the old request
				existing.controller.abort();
				requestMap.delete(requestKey);
			}

			// Create new AbortController for this request
			const controller = new AbortController();

			// Create the promise for this request
			const promise = api
				.post('/gp-bookings/v1/calculate-price', {
					data: pricingData,
					options: {
						signal: controller.signal,
						headers: {
							'X-Enable-Fetch-Cache': 'true',
						},
					},
				})
				.then((result) => {
					// Clean up from map on success
					requestMap.delete(requestKey);
					return result.price || 0;
				})
				.catch((error) => {
					// Clean up from map on error
					requestMap.delete(requestKey);

					// Ignore abort errors (expected when we cancel requests)
					if (error.name === 'AbortError') {
						return 0;
					}

					// eslint-disable-next-line no-console
					console.error(
						'GP Bookings: Price calculation error:',
						error
					);
					return 0;
				});

			// Store in map to track this in-flight request
			requestMap.set(requestKey, {
				promise,
				controller,
				params: paramsKey,
			});

			return promise;
		},
		[formId, serviceId]
	);

	const clearCache = useCallback(() => {
		// Abort any in-flight requests for this form (all fields)
		requestMap.forEach((value, key) => {
			if (key.startsWith(`${formId}:`)) {
				value.controller.abort();
				requestMap.delete(key);
			}
		});

		// Clear all pricing calculation cache entries
		invalidateCacheByPattern('/gp-bookings/v1/calculate-price');
	}, [formId]);

	return {
		calculatePrice,
		clearCache,
	};
}
